import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/constants";

function isChildActive(children, pathname) {
  return children?.some(
    (c) => pathname === c.to || pathname.startsWith(`${c.to}/`)
  );
}

function DesktopItem({ item }) {
  const location = useLocation();

  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        className={({ isActive }) =>
          `px-3 py-2 text-sm rounded-full transition ${
            isActive
              ? "text-[#28C0D8] bg-[#28C0D8]/10"
              : "text-white/70 hover:text-white hover:bg-white/5"
          }`
        }
        data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {item.label}
      </NavLink>
    );
  }

  const active = isChildActive(item.children, location.pathname);

  return (
    <div
      className="relative group"
      data-testid={`nav-group-${item.label.toLowerCase()}`}
    >
      <button
        type="button"
        className={`inline-flex items-center gap-1 px-3 py-2 text-sm rounded-full transition ${
          active
            ? "text-[#28C0D8] bg-[#28C0D8]/10"
            : "text-white/70 hover:text-white hover:bg-white/5"
        }`}
        aria-haspopup="true"
      >
        {item.label}
        <ChevronDown className="h-3.5 w-3.5 opacity-70 transition-transform group-hover:rotate-180" />
      </button>

      {/* invisible bridge so hover doesn't drop between button and panel */}
      <div className="absolute left-0 top-full h-3 w-full" />

      <div
        className="absolute left-0 top-[calc(100%+8px)] min-w-[280px] rounded-2xl border border-white/10 bg-[#0A0B0E] p-2 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.95)] ring-1 ring-black/40 opacity-0 translate-y-1 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto"
        role="menu"
      >
        {item.children.map((c) => (
          <NavLink
            key={c.to}
            to={c.to}
            className={({ isActive }) =>
              `flex flex-col gap-0.5 rounded-xl px-3 py-2.5 transition ${
                isActive
                  ? "bg-[#28C0D8]/10 text-[#28C0D8]"
                  : "text-white/80 hover:bg-white/5 hover:text-white"
              }`
            }
            data-testid={`nav-sublink-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="text-[14px] font-medium">{c.label}</span>
            {c.desc && (
              <span className="text-[11px] text-white/45 leading-snug">
                {c.desc}
              </span>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function MobileItem({ item, onNavigate }) {
  const location = useLocation();
  const active = item.children
    ? isChildActive(item.children, location.pathname)
    : false;
  const [expanded, setExpanded] = useState(active);

  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        onClick={onNavigate}
        className={({ isActive }) =>
          `block px-3 py-2.5 text-base rounded-lg ${
            isActive
              ? "text-[#28C0D8] bg-[#28C0D8]/10"
              : "text-white/80 hover:text-white hover:bg-white/5"
          }`
        }
        data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`w-full flex items-center justify-between px-3 py-2.5 text-base rounded-lg ${
          active
            ? "text-[#28C0D8] bg-[#28C0D8]/10"
            : "text-white/80 hover:text-white hover:bg-white/5"
        }`}
        data-testid={`nav-mobile-group-${item.label.toLowerCase()}`}
      >
        <span>{item.label}</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-1">
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                `block px-3 py-2 text-sm rounded-lg ${
                  isActive
                    ? "text-[#28C0D8] bg-[#28C0D8]/10"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
              data-testid={`nav-mobile-sublink-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0A0B0E]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="h-16 md:h-20 flex items-center justify-between gap-6">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="nav-logo-link"
          >
            <img
              src={BRAND.logoTransparent}
              alt="MD Supply Wellness"
              className="h-9 md:h-10 w-auto object-contain transition group-hover:opacity-90"
            />
            <div className="leading-tight">
              <p className="font-display text-[15px] md:text-base font-semibold tracking-tight text-white">
                MD Supply Wellness
              </p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#28C0D8] -mt-0.5">
                Bethesda · MD
              </p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <DesktopItem key={l.label} item={l} />
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="hidden xl:inline-flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:text-white rounded-full transition"
              data-testid="nav-phone-link"
            >
              <Phone className="h-4 w-4" />
              {BRAND.phone}
            </a>
            <Link
              to="/menu"
              className="btn-teal-glow inline-flex items-center rounded-full bg-[#28C0D8] px-5 py-2.5 text-sm font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC]"
              data-testid="nav-notify-btn"
            >
              Notify Me
            </Link>
          </div>

          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 p-2 text-white hover:bg-white/10"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div
            className="lg:hidden -mx-6 md:-mx-10 px-6 md:px-10 border-t border-white/10 py-4 space-y-1 bg-[#0A0B0E]/98 backdrop-blur-xl shadow-[0_12px_40px_-10px_rgba(0,0,0,0.8)] animate-fade-in"
            data-testid="nav-mobile-menu"
          >
            {NAV_LINKS.map((l) => (
              <MobileItem
                key={l.label}
                item={l}
                onNavigate={() => setOpen(false)}
              />
            ))}
            <div className="pt-2 flex items-center gap-2">
              <a
                href={`tel:${BRAND.phoneRaw}`}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white"
              >
                <Phone className="h-4 w-4" /> Call
              </a>
              <Link
                to="/menu"
                className="flex-1 inline-flex items-center justify-center rounded-full bg-[#28C0D8] px-4 py-2.5 text-sm font-semibold text-[#0A0B0E]"
              >
                Notify Me
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
